import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Alert, Modal, Input, message } from "antd";

import SectionLibrary from "../components/features/template-builder/SectionLibrary";
import TemplateCanvas from "../components/features/template-builder/TemplateCanvas";
import SectionConfigurator from "../components/features/template-builder/SectionConfigurator";
import TemplatePreview from "../components/features/template-builder/TemplatePreview";
import ActionBar from "../components/features/template-builder/ActionBar";
import { useTemplateEditor } from "../hooks/useTemplateEditor";
import { getSectionLibrary } from "../services/TemplateService";

/**
 * Template Builder page.
 *
 * Routes:
 *   /admin/template-builder           -> new template (empty canvas)
 *   /admin/template-builder/:templateId -> edit existing template (current version)
 *
 * Layout: three-pane (Library / Canvas / Preview) with a bottom drawer
 * (SectionConfigurator) that opens when a section is selected on the canvas.
 * The Template Builder is admin-only; templates are a single shared library.
 */
export default function TemplateBuilder() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const editor = useTemplateEditor();
  const [catalog, setCatalog] = useState(null); // {categories, sections} from backend
  const [generating, setGenerating] = useState(false);
  // Name prompt for Clone / Save as new.
  // action: "clone" | "saveAsNew"
  const [nameModal, setNameModal] = useState({ open: false, value: "", action: "clone" });

  // Load template on mount (or start a new one).
  useEffect(() => {
    if (templateId) {
      editor.loadTemplate(templateId);
    } else {
      editor.newTemplate({ name: "", specialty_tag: "behavioral_health" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // Fetch the read-only section library catalog. Backend singleton; one fetch per page mount.
  useEffect(() => {
    let cancelled = false;
    getSectionLibrary()
      .then((data) => {
        if (!cancelled) setCatalog(data);
      })
      .catch((err) => {
        // Non-fatal: SectionLibrary falls back to its inline placeholder so
        // the page renders even when the backend is unreachable.
        // eslint-disable-next-line no-console
        console.warn("Failed to fetch section library catalog:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { state } = editor;
  const selectedSection =
    state.selectedSectionIndex !== null && state.definition
      ? state.definition.sections[state.selectedSectionIndex]
      : null;

  const libraryEntryForSelected = selectedSection
    ? (catalog?.sections || []).find((s) => s.section_id === selectedSection.section_id)
    : null;

  const handleGenerate = async (description) => {
    setGenerating(true);
    try {
      await editor.generateFromDescription({
        description,
        specialty_tag: state.definition?.template?.specialty_tag,
        intended_visit_type: state.definition?.template?.intended_visit_type,
        name: state.definition?.template?.name,
      });
    } finally {
      setGenerating(false);
    }
  };

  const openClonePrompt = () =>
    setNameModal({ open: true, value: `${state.definition?.template?.name || "Template"} (copy)`, action: "clone" });

  const openSaveAsNewPrompt = () =>
    setNameModal({ open: true, value: `${state.definition?.template?.name || "Template"} (copy)`, action: "saveAsNew" });

  const submitNamePrompt = async () => {
    const newName = nameModal.value.trim();
    if (!newName) {
      message.warning("Enter a name.");
      return;
    }
    try {
      if (nameModal.action === "saveAsNew") {
        await editor.saveAsNew(newName);
      } else {
        await editor.cloneTemplate(newName);
      }
      setNameModal({ open: false, value: "", action: "clone" });
      message.success(`Created "${newName}".`);
    } catch (err) {
      message.error(err?.response?.data?.detail || err?.message || "Could not create the template.");
    }
  };

  const handleArchive = () => {
    Modal.confirm({
      title: "Archive this template?",
      content: "It will be hidden from the active list. You can restore it later.",
      okText: "Archive",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await editor.archiveTemplate();
          message.success("Template archived.");
          navigate("/admin/templates");
        } catch (err) {
          message.error(err?.response?.data?.detail || err?.message || "Could not archive.");
        }
      },
    });
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <ActionBar
        template={state.template}
        name={state.definition?.template?.name}
        onNameChange={editor.setName}
        specialtyTag={state.definition?.template?.specialty_tag}
        onSpecialtyChange={editor.setSpecialty}
        pendingChanges={state.pendingChanges}
        saving={state.saving}
        onSave={editor.save}
        onSaveAsNew={openSaveAsNewPrompt}
        onClone={openClonePrompt}
        onArchive={handleArchive}
        onGenerate={handleGenerate}
        generating={generating}
        onClearDraft={editor.clearDraft}
        sectionCount={state.definition?.sections?.length ?? 0}
        onRestore={editor.restoreVersion}
      />

      {state.validationErrors.length > 0 && (
        <Alert
          type="error"
          showIcon
          className="m-3"
          message="Validation errors"
          description={
            <ul className="text-sm">
              {state.validationErrors.map((e, i) => (
                <li key={i}>
                  <strong>{e.validator}:</strong> {e.message}
                </li>
              ))}
            </ul>
          }
        />
      )}

      <main className="flex-1 flex min-h-0">
        <SectionLibrary
          catalog={catalog}
          onAddSection={editor.addSection}
          specialtyTag={state.definition?.template?.specialty_tag}
          addedSectionIds={(state.definition?.sections || []).map((s) => s.section_id)}
        />

        <TemplateCanvas
          sections={state.definition?.sections || []}
          selectedIndex={state.selectedSectionIndex}
          onSelectSection={editor.selectSection}
          onRemoveSection={editor.removeSection}
          onReorderSections={editor.reorderSections}
        />

        <TemplatePreview
          previewState={state.previewState}
          pendingChanges={state.pendingChanges}
          onRefreshPreview={editor.refreshPreview}
        />
      </main>

      <SectionConfigurator
        open={selectedSection !== null}
        onClose={() => editor.selectSection(null)}
        section={selectedSection}
        libraryEntry={libraryEntryForSelected}
        onChange={(partial) =>
          editor.updateSectionAt(state.selectedSectionIndex, partial)
        }
      />

      <Modal
        title="Name the new template"
        open={nameModal.open}
        onCancel={() => setNameModal({ open: false, value: "", action: "clone" })}
        onOk={submitNamePrompt}
        okText="Create"
      >
        <Input
          value={nameModal.value}
          onChange={(e) => setNameModal((m) => ({ ...m, value: e.target.value }))}
          placeholder="Template name"
          onPressEnter={submitNamePrompt}
        />
      </Modal>
    </div>
  );
}
